import { config } from '@ps/config';

export function internalBuildPopstoreUrl(
  baseUrl: string,
  origin: string,
  page: string,
  ...pathSegments: string[]
): URL {
  const base = baseUrl.replace(/\/+$/, '');

  const pathParts = [page, ...pathSegments]
    .flatMap(p => p?.split('/'))
    .filter(Boolean)
    .map(s => encodeURIComponent(s))
    .join('/');

  return new URL(`${base}/${pathParts}`, origin);
}

/**
 * Resolve a site-relative path (optionally carrying a query string) to an
 * ABSOLUTE URL against the running origin. Required whenever a URL is handed to
 * the native bridge / an external browser: on tagged & preview deploys
 * `config.url` is `'/'` (relative), so the naive ``${config.url}${path}`` yields
 * a schemeless `//path` the OS can't open (`file:////path`). Resolving
 * `config.url` against `window.location.origin` fixes both cases — a relative
 * `'/'` picks up the live host, while an absolute prod `config.url` wins over
 * the base. Unlike {@link popstoreUrl}, the path is passed through the `URL`
 * constructor untouched, so an existing query string survives.
 *
 * Browser-only (reads `window.location`) — call from client handlers.
 */
export function absoluteAppUrl(path: string): string {
  const base = new URL(config.url, window.location.origin);
  return new URL(path, base).toString();
}

export function popstoreUrl(page: string, ...pathSegments: string[]): URL {
  // `location` is only available in the browser. On the server (SSR /
  // RSC) fall back to a placeholder origin — the URL constructor only
  // uses the second argument when the first is a relative path. In
  // production deploys `config.url` is absolute (`https://pop.store/`)
  // and the placeholder is ignored entirely. For revision-tag deploys
  // where `config.url === '/'`, the SSR-built URL will have the
  // placeholder host; callers that need the value in SSR HTML should
  // use `.pathname` rather than `.toString()`.
  const origin =
    typeof location !== 'undefined' ? location.origin : 'http://localhost';
  return internalBuildPopstoreUrl(config.url, origin, page, ...pathSegments);
}
